import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import ErrorModal from "@meetyoo/components/src/components/Modal/ErrorModal";

import EnhancedTable from "@meetyoo/components/src/components/Table/EnhancedTable";
import { setFilter, setErrorMessage, requestLedgerEntries, deleteLedgerEntry } from "../../../actions/billing";

import BillingLedgerEntryTableRow, { headRows } from "../BillingLedgerEntryTableRow";
import BillingLedgerEntryFilter from "../BillingLedgerEntryFilter";

const orderValues = {
  CONFERENCE_ID: "conferenceId",
  STAT: "stat",
  START_TIME: "startTime",
  END_TIME: "endTime",
  CONTRACT_NO: "contractNo",
  ERROR_CODE: "errorCode",
  QUANTITY: "quantity",
  NAME: "name",
  REQUEST_FROM: "requestFrom",
};

const returnEnumOrderValue = value => Object.keys(orderValues).filter(or => orderValues[or] === value && or)[0];

export const BillingLedgerEntriesComponent = ({
  requestBillingInProgress,
  ledgerEntries,
  errorMessage,
  translate,
  setErrorMessageFn,
  requestLedgerEntriesFn,
  ledgerLength,
  filter,
  setFilterFn,
  deleteLedgerEntryFn,
}) => {
  const [mounted, setMounted] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [nothingFound, setNothingFound] = useState(false);
  const [resetPage, setResetPage] = useState(false);
  const [madeRequest, setMadeRequest] = useState(false);

  useEffect(() => {
    if (!mounted && ledgerEntries.length === 0) {
      setMadeRequest(true);
      setMounted(true);
      requestLedgerEntriesFn(filter);
    }
    setResetPage(false);
    setOpenErrorModal(errorMessage !== "");
    setNothingFound(!requestBillingInProgress && ledgerEntries.length === 0 && madeRequest && errorMessage === "");
  }, [
    requestLedgerEntriesFn,
    ledgerEntries.length,
    ledgerEntries,
    mounted,
    openErrorModal,
    errorMessage,
    ledgerLength,
    filter,
    setFilterFn,
    requestBillingInProgress,
    madeRequest,
  ]);

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessageFn("");
    setNothingFound(false);
    setMadeRequest(false);
  };

  const newEntries = [];
  for (let i = 0; i < filter.page * filter.amountOfLedgers; i += 1) {
    newEntries.push({});
  }

  return [
    <EnhancedTable
      resetPage={resetPage}
      setReturnPage={page => {
        setFilterFn({ page });
        setMadeRequest(true);
        requestLedgerEntriesFn({ ...filter, page });
      }}
      setReturnRowsPerPage={amountOfLedgers => {
        setFilterFn({ amountOfLedgers });
        setMadeRequest(true);
        requestLedgerEntriesFn({ ...filter, amountOfLedgers });
      }}
      sizeOfRows={ledgerLength}
      setReturnOrderBy={(ordering, direction) => {
        setFilterFn({ orderBy: returnEnumOrderValue(ordering), orderDirection: direction.toUpperCase(), page: 0 });
        setMadeRequest(true);
        setResetPage(true);
        requestLedgerEntriesFn({
          ...filter,
          orderBy: returnEnumOrderValue(ordering),
          orderDirection: direction.toUpperCase(),
          page: 0,
        });
      }}
      key="ledger-entries"
      title={translate.ledgerntries}
      rows={newEntries.concat(ledgerEntries)}
      headRows={headRows(translate)}
      tableRowComponent={BillingLedgerEntryTableRow}
      inProgress={requestBillingInProgress}
      deleteHandler={rowsToDelete => {
        if (window.confirm("Are you sure to delete " + rowsToDelete.length + " entries?")) {
          rowsToDelete.forEach(r => deleteLedgerEntryFn(r.id));
          requestLedgerEntriesFn(filter);
        }
      }}
      filterChildren={
        <BillingLedgerEntryFilter
          getLedgerEntriesHandler={requestLedgerEntriesFn}
          setResetPage={setResetPage}
          setMadeRequest={setMadeRequest}
          requestBillingInProgress={requestBillingInProgress}
        />
      }
    />,
    <ErrorModal
      key="error"
      handleCloseErrorModal={handleCloseErrorModal}
      openErrorModal={openErrorModal}
      errorMessage={errorMessage}
    />,
    <ErrorModal
      key="nothing-found"
      handleCloseErrorModal={handleCloseErrorModal}
      openErrorModal={nothingFound}
      errorMessage="Nothing Found"
    />,
  ];
};

BillingLedgerEntriesComponent.defaultProps = {
  ledgerEntries: [],
  errorMessage: "",
};

BillingLedgerEntriesComponent.propTypes = {
  requestBillingInProgress: PropTypes.bool.isRequired,
  ledgerEntries: PropTypes.array,
  errorMessage: PropTypes.string,
  setErrorMessageFn: PropTypes.func.isRequired,
  requestLedgerEntriesFn: PropTypes.func.isRequired,
  deleteLedgerEntryFn: PropTypes.func.isRequired,
  ledgerLength: PropTypes.number.isRequired,
  filter: PropTypes.object.isRequired,
  setFilterFn: PropTypes.func.isRequired,
  translate: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  requestBillingInProgress: state.billing.requestBillingInProgress,
  ledgerEntries: state.billing.ledgerEntries,
  errorMessage: state.billing.errorMessage,
  ledgerLength: state.billing.ledgerLength,
  filter: state.billing.filter,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  setErrorMessageFn: errorMessage => dispatch(setErrorMessage(errorMessage)),
  setFilterFn: filter => dispatch(setFilter(filter)),
  deleteLedgerEntryFn: id => dispatch(deleteLedgerEntry(id)),
  requestLedgerEntriesFn: filter => dispatch(requestLedgerEntries(filter)),
});

const BillingLedgerEntries = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingLedgerEntriesComponent);

export default BillingLedgerEntries;
